import React, { useState, memo, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { RefreshCw, ChevronRight, Moon, Droplets, Wind, Gauge, ExternalLink } from 'lucide-react';
import { ErrorBoundary, Button, Skeleton } from '../common';
import CurrentConditionsEnhanced from '../weather/CurrentConditionsEnhanced';
import LiveBeachCameras from '../weather/LiveBeachCameras';
import WeatherMaps from '../weather/WeatherMaps';
import SurfFishingReport from '../weather/SurfFishingReport';
import HourlyForecast from '../weather/HourlyForecast';
import DailyForecast from '../weather/DailyForecast';
import SunMoon from '../weather/SunMoon';
import MoonPhaseCalendar from '../weather/MoonPhaseCalendar';
import TideSchedule from '../weather/TideSchedule';
import WeatherSkeleton from '../weather/WeatherSkeleton';
import { getWeatherIcon, getTimeAwareWeatherIcon, getWeatherDescription } from '../../constants/weatherCodes';

// Helper function to convert wind degrees to direction
const getWindDirection = (degrees) => {
  const directions = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'];
  const index = Math.round(degrees / 22.5) % 16;
  return directions[index];
};

const Weather = ({ 
  weather, 
  tides, 
  waterTemp,
  waterTempStation,
  currentWaterLevel,
  moonData, 
  loadingWeather, 
  weatherError, 
  onFetchWeather,
  flagStatus,
  onUpdateFlag
}) => {
  const [isPulling, setIsPulling] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const [touchStartY, setTouchStartY] = useState(0);
  
  // Touch event handlers for pull-to-refresh
  const handleTouchStart = useCallback((e) => {
    setTouchStartY(e.touches[0].clientY);
  }, []);
  
  const handleTouchMove = useCallback((e) => {
    if (window.scrollY === 0) {
      const touchY = e.touches[0].clientY;
      const distance = touchY - touchStartY;
      if (distance > 0) {
        setIsPulling(true);
        setPullDistance(Math.min(distance, 150));
      }
    }
  }, [touchStartY]);
  
  const handleTouchEnd = useCallback(() => {
    if (pullDistance > 80) {
      onFetchWeather();
    }
    setIsPulling(false);
    setPullDistance(0);
  }, [pullDistance, onFetchWeather]);
  // Memoize expensive tide calculations
  const processedTides = useMemo(() => {
    if (!tides || tides.length === 0) return [];
    return tides.slice(0, 8).map((tide, idx) => {
      const tideTime = new Date(tide.t);
      const isHigh = tide.type === 'H';
      const nextTide = tides[idx + 1];
      const timeDiff = nextTide ? (new Date(nextTide.t) - tideTime) / (1000 * 60 * 60) : null;
      return { ...tide, tideTime, isHigh, timeDiff };
    });
  }, [tides]);
  
  // Memoize callback for flag update
  const handleUpdateFlag = useCallback(() => {
    onUpdateFlag();
  }, [onUpdateFlag]);
  return (
    <div className="space-y-4"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Pull to refresh indicator */}
      {isPulling && (
        <div 
          className="fixed top-0 left-0 right-0 flex justify-center items-center bg-orange-100 dark:bg-orange-900/20 transition-all z-50"
          style={{ height: `${pullDistance}px`, opacity: pullDistance / 150 }}
        >
          <RefreshCw className={`w-6 h-6 text-orange-600 dark:text-orange-400 ${pullDistance > 80 ? 'animate-spin' : ''}`} />
          <span className="ml-2 text-sm text-orange-600 dark:text-orange-400">
            {pullDistance > 80 ? 'Release to refresh' : 'Pull to refresh'}
          </span>
        </div>
      )}
      <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4 animate-fade-in">🌤️ Cape San Blas Weather</h2>
      
      <ErrorBoundary>
        {loadingWeather ? (
          <WeatherSkeleton />
        ) : weatherError ? (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <p className="text-red-800 dark:text-red-200 font-medium">Unable to load weather data</p>
            <p className="text-red-600 dark:text-red-300 text-sm mt-1">{weatherError}</p>
            <Button 
              onClick={onFetchWeather}
              variant="primary"
              size="small"
              className="mt-3"
            >
              Try Again
            </Button>
          </div>
        ) : (
          <>
            {/* Current Conditions - Enhanced with all requested features */}
            <CurrentConditionsEnhanced
              weather={weather}
              waterTemp={waterTemp}
              waterTempStation={waterTempStation}
              currentWaterLevel={currentWaterLevel}
            />

            {/* Beach Flags */}
            {flagStatus && (
              <div className="bg-white/95 dark:bg-gray-800/95 backdrop-blur rounded-xl shadow-lg p-5 border border-orange-100 dark:border-gray-700 animate-on-scroll card-hover hover-lift">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-semibold text-lg dark:text-gray-100">🚩 Current Beach Flag</h3>
                  <button
                    onClick={handleUpdateFlag}
                    className="text-sm text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1 ripple"
                    type="button"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Update
                  </button>
                </div>
                <div className="text-center py-4">
                  <div className="text-5xl mb-3 relative inline-block pulse-glow rounded-full p-4">
                    {flagStatus.color}
                  </div>
                  <p className="text-xl font-semibold text-gray-800 dark:text-gray-100">{flagStatus.text}</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">{flagStatus.details}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
                    Last updated: {flagStatus.lastUpdated}
                    {flagStatus.source && <span className="block">{flagStatus.source}</span>}
                  </p>
                </div>
                <div className="flex gap-2 flex-wrap justify-center">
                  <a 
                    href="https://www.facebook.com/sgcfire/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-center text-orange-600 dark:text-orange-400 text-sm hover:underline"
                  >
                    Facebook Updates
                  </a>
                  <span className="text-gray-400">•</span>
                  <a 
                    href="https://www.visitgulf.com/things-to-do/beaches/beach-safety/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-center text-orange-600 dark:text-orange-400 text-sm hover:underline"
                  >
                    Gulf County Beach Safety
                  </a>
                </div>
              </div>
            )}

            {/* Enhanced Hourly Forecast - 24 hours */}
            <HourlyForecast weather={weather} />

            {/* Enhanced 7-Day Forecast */}
            <DailyForecast weather={weather} />

            {/* Sun & Moon - Enhanced */}
            <SunMoon weather={weather} moonData={moonData} />

            {/* Moon Phase Calendar */}
            <MoonPhaseCalendar moonData={moonData} />

            {/* Enhanced Tide Information */}
            <TideSchedule processedTides={processedTides} currentWaterLevel={currentWaterLevel} />

            {/* Windy.com Weather Maps */}
            <WeatherMaps />

            {/* Live Beach Cameras */}
            <LiveBeachCameras />

            {/* Surf Fishing Report */}
            <SurfFishingReport 
              waterTemp={waterTemp}
              weather={weather}
              processedTides={processedTides}
              moonData={moonData}
            />
          </>
        )}
      </ErrorBoundary>
    </div>
  );
};

Weather.propTypes = {
  weather: PropTypes.shape({
    current: PropTypes.shape({
      temperature_2m: PropTypes.number,
      relative_humidity_2m: PropTypes.number,
      apparent_temperature: PropTypes.number,
      weather_code: PropTypes.number,
      wind_speed_10m: PropTypes.number,
      wind_direction_10m: PropTypes.number,
      pressure_msl: PropTypes.number,
      cloud_cover: PropTypes.number
    }),
    hourly: PropTypes.shape({
      time: PropTypes.arrayOf(PropTypes.string),
      temperature_2m: PropTypes.arrayOf(PropTypes.number),
      weather_code: PropTypes.arrayOf(PropTypes.number),
      precipitation_probability: PropTypes.arrayOf(PropTypes.number),
      wind_speed_10m: PropTypes.arrayOf(PropTypes.number)
    }),
    daily: PropTypes.shape({
      time: PropTypes.arrayOf(PropTypes.string),
      temperature_2m_max: PropTypes.arrayOf(PropTypes.number),
      temperature_2m_min: PropTypes.arrayOf(PropTypes.number),
      weather_code: PropTypes.arrayOf(PropTypes.number),
      sunrise: PropTypes.arrayOf(PropTypes.string),
      sunset: PropTypes.arrayOf(PropTypes.string),
      uv_index_max: PropTypes.arrayOf(PropTypes.number),
      precipitation_sum: PropTypes.arrayOf(PropTypes.number),
      precipitation_probability_max: PropTypes.arrayOf(PropTypes.number),
      wind_speed_10m_max: PropTypes.arrayOf(PropTypes.number)
    })
  }),
  tides: PropTypes.arrayOf(PropTypes.shape({
    t: PropTypes.string,
    v: PropTypes.number,
    type: PropTypes.string
  })),
  waterTemp: PropTypes.number,
  currentWaterLevel: PropTypes.number,
  moonData: PropTypes.shape({
    current: PropTypes.shape({
      emoji: PropTypes.string,
      name: PropTypes.string,
      illumination: PropTypes.number,
      moonrise: PropTypes.string,
      moonset: PropTypes.string
    }),
    week: PropTypes.arrayOf(PropTypes.shape({
      date: PropTypes.instanceOf(Date),
      phase: PropTypes.shape({
        emoji: PropTypes.string,
        name: PropTypes.string,
        illumination: PropTypes.number
      }),
      moonrise: PropTypes.string,
      moonset: PropTypes.string
    }))
  }),
  loadingWeather: PropTypes.bool.isRequired,
  weatherError: PropTypes.string,
  onFetchWeather: PropTypes.func.isRequired,
  flagStatus: PropTypes.shape({
    color: PropTypes.string,
    text: PropTypes.string,
    details: PropTypes.string,
    lastUpdated: PropTypes.string
  }),
  onUpdateFlag: PropTypes.func.isRequired
};

export default memo(Weather);